import type ExcelJS from 'exceljs';
import { XLSX_BRAND as BRAND, argb } from '@/lib/xlsx-brand';
import type { BuilderSheet } from './report-builder-state';

/**
 * A built report as a Hygge-looking workbook.
 *
 * The Builder had no spreadsheet at all before this — its one Export button
 * opened a print window — so this file is not replacing anything a user ever
 * downloaded. What it has to beat is the shape a plain writer would produce:
 * every row at the same weight, so a person's total and the four project rows
 * under it are five identical lines of text, told apart only by a blank second
 * column. On screen that total is bold; a plain sheet says nothing.
 *
 * `exceljs` rather than the `xlsx` package the other exports use, because
 * `xlsx@0.18.5` is the community build and it does not WRITE cell styles — that
 * path can only ever produce the flat sheet described above. ExcelJS is already
 * a dependency here and there is already a house pattern for it, in the absence
 * schedule's export: frozen header, brand palette, column widths. [HW-877]
 *
 * CSV deliberately keeps the old writer: a CSV carries no styling at all, so
 * there is nothing here for it to gain. It opens with the same lines about the
 * report as this workbook, before its column headings, so a program reading it
 * finds the headings a few lines down rather than on the first line. [HW-953]
 */

const solid = (hex: string): ExcelJS.FillPattern => ({
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: argb(hex) },
});

const HAIRLINE = { style: 'thin' as const, color: { argb: argb(BRAND.border) } };

/**
 * Builds the workbook. Pure enough to test: it touches no DOM, so a test can
 * assert which rows come out painted — which is the whole point of the file and
 * the one thing a reader cannot check by opening it in a spreadsheet.
 */
export async function buildBuilderWorkbook(sheet: BuilderSheet): Promise<ExcelJS.Workbook> {
  // Dynamic, as in the schedule export: the workbook writer and its style
  // runtime are ~250 KB gzipped, and nobody should download them for a page
  // they only read.
  const Lib = (await import('exceljs')).default;

  const wb = new Lib.Workbook();
  wb.creator = 'Hygge';
  wb.created = new Date();

  const lastCol = sheet.columns.length;
  // Title, then one row per line of what shaped the report, then the headings.
  const headerRowNumber = 2 + sheet.about.length;
  const bodyStart = headerRowNumber + 1;
  const ws = wb.addWorksheet('Time report', {
    // The header row stays put while the reader scrolls: a report of forty
    // groups is exactly the document where a column of bare numbers stops
    // saying which column it is. A frozen pane is the rows above a line, so
    // the lines about the report stay in view with it.
    views: [{ state: 'frozen', ySplit: headerRowNumber }],
  });

  // ── Title ─────────────────────────────────────────────────────────────
  // The document has to name itself: it leaves the building, and a file called
  // "Hours / Share" with no heading is a report about nothing in particular.
  ws.mergeCells(1, 1, 1, lastCol);
  const title = ws.getCell(1, 1);
  title.value = sheet.title;
  title.fill = solid(BRAND.teal);
  title.font = { name: 'Calibri', size: 14, bold: true, color: { argb: argb(BRAND.cream) } };
  title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(1).height = 28;

  // ── What shaped the report ────────────────────────────────────────────
  // The period, the filters and what is still open, as the PDF prints them —
  // without these the workbook of a filtered August reads as the whole
  // company's, and says so nowhere but its file name. [HW-953]
  // The text is neither merged nor wrapped: an unwrapped cell runs on over the
  // empty cells beside it, where a merged one would need a row height Excel
  // does not work out for it.
  sheet.about.forEach((note, index) => {
    const target = ws.getRow(2 + index);
    target.values = [note.label, note.text];
    const label = target.getCell(1);
    label.font = { name: 'Calibri', size: 10, bold: true, color: { argb: argb(BRAND.foreground) } };
    label.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    const text = target.getCell(2);
    text.font = { name: 'Calibri', size: 10, color: { argb: argb(BRAND.mutedText) } };
    text.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  });

  // ── Header ────────────────────────────────────────────────────────────
  const header = ws.getRow(headerRowNumber);
  header.values = sheet.columns.map((column) => column.header);
  header.height = 22;
  header.eachCell((cell, col) => {
    cell.fill = solid(BRAND.teal);
    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: argb(BRAND.cream) } };
    cell.alignment = {
      vertical: 'middle',
      // Numbers right, labels left, in the header as in the body — a heading
      // that sits over the wrong edge of its column reads as another column.
      horizontal: col > sheet.groupColumnCount ? 'right' : 'left',
      indent: 1,
    };
  });

  // A share is written as the number 15, so the column can be worked with —
  // but a column headed "Share" holding 15 beside one headed "Hours" holding
  // 70 reads as two quantities of the same kind. The format puts the sign back
  // without turning the cell into text. (CSV carries no formats; there the
  // heading is all the reader gets.)
  sheet.columns.forEach((column, index) => {
    if (column.percent) ws.getColumn(index + 1).numFmt = '0"%"';
  });

  // ── Body ──────────────────────────────────────────────────────────────
  sheet.rows.forEach((row, index) => {
    const target = ws.getRow(bodyStart + index);
    target.values = row.cells;
    target.height = 18;
    target.eachCell((cell, col) => {
      const isLabel = col <= sheet.groupColumnCount;
      cell.alignment = { vertical: 'middle', horizontal: isLabel ? 'left' : 'right', indent: 1 };
      cell.border = { bottom: HAIRLINE };
      if (row.subtotal) {
        // What the ticket's reader was missing: a group's total has to be
        // readable AS a total, or it is one more row and the column appears not
        // to add up. Tinted and bold, with a rule above it, so a group reads as
        // a block that opens with its own figure.
        cell.fill = solid(BRAND.beige);
        cell.font = {
          name: 'Calibri',
          size: 11,
          bold: true,
          color: { argb: argb(BRAND.foreground) },
        };
        cell.border = { top: HAIRLINE, bottom: HAIRLINE };
      } else {
        cell.font = { name: 'Calibri', size: 11, color: { argb: argb(BRAND.mutedText) } };
      }
    });
  });

  // ── Grand total ───────────────────────────────────────────────────────
  const totalRow = ws.getRow(bodyStart + sheet.rows.length);
  totalRow.values = sheet.total;
  totalRow.height = 22;
  totalRow.eachCell((cell, col) => {
    cell.fill = solid(BRAND.cream);
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: argb(BRAND.foreground) } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: col <= sheet.groupColumnCount ? 'left' : 'right',
      indent: 1,
    };
    cell.border = { top: { style: 'medium', color: { argb: argb(BRAND.teal) } } };
  });

  sheet.columns.forEach((column, index) => {
    ws.getColumn(index + 1).width = column.width;
  });

  return wb;
}

/**
 * Hands the workbook to the browser.
 *
 * Split from the building above so the styling can be tested in this repo's
 * node-environment vitest, which has no `URL.createObjectURL`. Blob and a
 * temporary anchor rather than a FileSaver dependency, as the schedule export
 * does it.
 */
export async function downloadBuilderWorkbook(sheet: BuilderSheet): Promise<void> {
  const wb = await buildBuilderWorkbook(sheet);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${sheet.filename}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
